import sortBy from 'lodash/sortBy';

import type { LessonActivitiesFragment } from '../../lesson-builder.graphql.generated';
import {
  BaseSectionOverview,
  SectionOrSubSection,
} from 'approot/activity-builder/activity-builder.constants';
import type { InteractiveLessonSectionFragment } from '../../lesson-builder.graphql.generated';
import { ComponentActivity } from '../component.constants';
import { ACTIVITIES_COMPONENT_TYPE_ACTIVITY } from 'global/constants';
import { StudentSubSectionOverview } from 'approot/activity-builder/student/student.constants';
import type { Pluck } from 'lib/gql/pluck';

export function getActivityNumber(
  sectionId: string,
  sections: {
    id: string;
    sort: string;
  }[]
) {
  const sortedSections = sortBy(sections, 'sort');
  return sortedSections.findIndex(s => s.id === sectionId) + 1;
}

export function findActivity(
  section: SectionOrSubSection | undefined
): ComponentActivity | undefined {
  if (!section) return;

  for (let i = 0; i < section.components.length; i++) {
    if (section.components[i].type === ACTIVITIES_COMPONENT_TYPE_ACTIVITY) {
      return section.components[i] as ComponentActivity;
    }
  }
  return undefined;
}

export const getActivityDetails = ({
  section,
  sectionsOverview,
}: {
  section: SectionOrSubSection | null | undefined;
  sectionsOverview: BaseSectionOverview[] | null | undefined;
}): {
  activityId: string | null;
  currentStep: number;
  totalSteps: number;
  progressLabel: string;
  differentiation: number | null;
  parentSectionId: string | null;
  isCheckPoint: boolean;
} => {
  let activityId: string | null = null;
  let totalSteps = 1;
  let currentStep = 1;
  let differentiation: number | null = 0;
  let parentSectionId: string | null = null;
  let isCheckPoint = false;

  if (section && sectionsOverview) {
    const activityComponent = findActivity(section);
    parentSectionId = activityComponent
      ? activityComponent.sectionId
      : (section as Pluck<InteractiveLessonSectionFragment, 'subSections'>)
          .parentSectionId;
    const parentSection = sectionsOverview.find(s => s.id === parentSectionId);

    if (activityComponent) {
      activityId = activityComponent.id;
      const subSections =
        (section as InteractiveLessonSectionFragment).subSections || [];
      totalSteps = subSections.length + 1;
      differentiation =
        activityComponent.properties.activitySettings.differentiation;
      isCheckPoint = !!activityComponent.properties.activitySettings.checkPoint;
    } else if (parentSection) {
      const overviews = (
        parentSection.subSectionsOverview
          ? sortBy(parentSection.subSectionsOverview, 'sort')
          : []
      ) as
        | Pluck<
            LessonActivitiesFragment,
            'sectionsOverview.subSectionsOverview'
          >[]
        | StudentSubSectionOverview[];
      const subSectionIndex =
        overviews.findIndex(s => s.id === section.id) || 0;
      activityId = parentSection.activityId || null;
      totalSteps = overviews.length + 1;
      currentStep = subSectionIndex + 2;
      differentiation = parentSection.differentiation;
      isCheckPoint =
        'checkPoint' in parentSection ? !!parentSection.checkPoint : false;
    }
  }

  return {
    activityId,
    currentStep,
    totalSteps,
    progressLabel: `Part ${currentStep} of ${totalSteps}`,
    differentiation,
    parentSectionId,
    isCheckPoint,
  };
};
